import { doc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { pricingData } from '../data/pricingData';

/**
 * Category definitions for VendorScout beta
 * Maps category IDs to display names
 */
const categories = {
  rmm: {
    id: 'rmm',
    name: 'RMM / Remote Monitoring',
    description: 'Remote monitoring and management tools for IT administrators'
  },
  antivirus: {
    id: 'antivirus',
    name: 'Antivirus / EDR',
    description: 'Endpoint protection, antivirus, and endpoint detection & response'
  },
  backup: {
    id: 'backup',
    name: 'Backup & Recovery',
    description: 'Data backup, disaster recovery, and business continuity solutions'
  },
  crm: {
    id: 'crm',
    name: 'CRM',
    description: 'Customer relationship management platforms'
  },
  project: {
    id: 'project',
    name: 'Project Management',
    description: 'Project management and team collaboration tools'
  },
  hr: {
    id: 'hr',
    name: 'HR / Payroll',
    description: 'Human resources and payroll management systems'
  },
  security: {
    id: 'security',
    name: 'Security / SIEM',
    description: 'Security information and event management, identity management'
  }
};

/**
 * Seed categories to Firestore
 */
export async function seedCategories(onProgress) {
  const categoryIds = Object.keys(categories);
  let seeded = 0;

  for (const catId of categoryIds) {
    const category = categories[catId];
    const docRef = doc(db, 'categories', catId);
    await setDoc(docRef, category, { merge: true });
    seeded++;
    onProgress?.(`Seeded category: ${category.name} (${seeded}/${categoryIds.length})`);
  }

  return seeded;
}

/**
 * Seed software pricing data to Firestore
 */
export async function seedSoftware(onProgress) {
  const softwareIds = Object.keys(pricingData);
  let seeded = 0;

  for (const swId of softwareIds) {
    const software = pricingData[swId];
    const docRef = doc(db, 'software', swId);

    // Prepare the document data
    const docData = {
      ...software,
      // Ensure lastUpdated is a proper timestamp string
      lastUpdated: software.lastUpdated || new Date().toISOString().split('T')[0]
    };

    await setDoc(docRef, docData, { merge: true });
    seeded++;
    onProgress?.(`Seeded software: ${software.name} (${seeded}/${softwareIds.length})`);
  }

  return seeded;
}

/**
 * Seed all pricing data (categories + software)
 */
export async function seedAllPricingData(onProgress) {
  onProgress?.('Starting database seed...');

  // Seed categories first
  onProgress?.('Seeding categories...');
  const categoryCount = await seedCategories(onProgress);

  // Seed software
  onProgress?.('Seeding software pricing data...');
  const softwareCount = await seedSoftware(onProgress);

  onProgress?.(`Complete! Seeded ${categoryCount} categories and ${softwareCount} software products.`);

  return { categoryCount, softwareCount };
}

/**
 * Get current counts from Firestore
 */
export async function getCurrentCounts() {
  const [categoriesSnapshot, softwareSnapshot] = await Promise.all([
    getDocs(collection(db, 'categories')),
    getDocs(collection(db, 'software'))
  ]);

  return {
    categories: categoriesSnapshot.size,
    software: softwareSnapshot.size
  };
}

/**
 * Clear all software data (use with caution!)
 */
export async function clearSoftwareData(onProgress) {
  const snapshot = await getDocs(collection(db, 'software'));
  let deleted = 0;

  for (const docSnap of snapshot.docs) {
    await deleteDoc(docSnap.ref);
    deleted++;
    onProgress?.(`Deleted ${deleted}/${snapshot.size}...`);
  }

  return deleted;
}

/**
 * Get available pricing data stats (from local file)
 */
export function getLocalDataStats() {
  const softwareList = Object.values(pricingData);
  const categoriesUsed = [...new Set(softwareList.map(s => s.category))];

  return {
    totalSoftware: softwareList.length,
    totalCategories: categoriesUsed.length,
    byCategory: categoriesUsed.reduce((acc, cat) => {
      acc[cat] = softwareList.filter(s => s.category === cat).length;
      return acc;
    }, {}),
    softwareList: softwareList.map(s => ({ id: s.id, name: s.name, category: s.category }))
  };
}
